<template>
    <div id="companies-view">
        <v-data-table
            locale="en-UK"
            class="elevation-1"
            loading-text="Loading... Please wait"
            no-data-text="No such company"
            :headers="headers"
            :items="companies"
            :footer-props="footerProps"
            :loading="loading"
            :items-per-page.sync="itemsPerPage"
            :sort-by.sync="searchMode ? [''] : sortBy"
            :sort-desc.sync="sortDesc"
            @update:items-per-page="getNextPage(1)"
            @update:page="getNextPage($event)">
            <template v-slot:top>

                <v-toolbar flat>
                    <v-toolbar-title>Companies {{ companiesUtils ? `(${companiesUtils.length})` : '' }}</v-toolbar-title>
                    <v-divider class="mx-4" inset vertical />
                    <v-spacer />

                    <v-dialog
                        v-model="editCompany"
                        fullscreen
                        hide-overlay
                        transition="dialog-bottom-transition">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn color="primary" dark v-bind="attrs" v-on="on">New Company</v-btn>
                        </template>
                        <v-card id="companies-view-edit">
                            <v-toolbar dark color="primary">
                                <v-btn icon dark @click="close">
                                    <v-icon>mdi-close</v-icon>
                                </v-btn>
                                <v-toolbar-title>
                                    <span class="headline">{{ formTitle }}</span>
                                </v-toolbar-title>
                                <v-spacer />
                                <v-toolbar-items v-show="mdAndUp">
                                    <v-btn :disabled="loading" text @click="save">Save</v-btn>
                                </v-toolbar-items>
                            </v-toolbar>
                            <v-card-text>
                                <v-form :disabled="loading" lazy-validation ref="dialogForm">
                                    <v-container id="dialog-container">
                                        <v-row justify="center" align="center">
                                            <v-col cols="12" sm="12" md="4">
                                                <v-text-field
                                                    :disabled="loading"
                                                    :rules="titleRule"
                                                    v-model="editedItem.naziv"
                                                    label="Naziv">
                                                </v-text-field>
                                            </v-col>
                                            <v-col cols="12" sm="12" md="2">
                                                <v-select
                                                    v-model="editedItem.grad"
                                                    :disabled="loading"
                                                    :rules="cityRule"
                                                    :items="cities"
                                                    label="Grad"
                                                    @focus="cities = [...companiesUtils.cities]"
                                                    @change="searchCity = ''"
                                                    no-data-text="Please add new city">
                                                    <template v-slot:prepend-item>
                                                        <v-container>
                                                            <v-text-field
                                                                v-model="searchCity"
                                                                @input="filterCity"
                                                                :placeholder="cities.length ? 'Search cities' : 'Add new city'"
                                                                solo
                                                                dense
                                                                clearable
                                                                hide-details>
                                                            </v-text-field>
                                                        </v-container>
                                                    </template>

                                                    <template v-slot:item="{ item }">
                                                        <v-hover>
                                                            <v-list-item class="px-0">
                                                                <v-list-item-content>
                                                                    <v-list-item-title>{{ item }}</v-list-item-title>
                                                                </v-list-item-content>
                                                                <v-list-item-action>
                                                                    <v-icon small right @click="cityDialog(item)">
                                                                        mdi-close
                                                                    </v-icon>
                                                                </v-list-item-action>
                                                            </v-list-item>
                                                        </v-hover>
                                                    </template>

                                                    <template v-if="!cities.length && searchCity" v-slot:append-item>
                                                        <div class="px-3 py-1">
                                                            <v-chip
                                                                class="ma-1"
                                                                color="primary"
                                                                close
                                                                close-icon="mdi-plus-circle"
                                                                @click:close="addOrDeleteCity(searchCity, 'add')">
                                                                {{ searchCity }}
                                                            </v-chip>
                                                        </div>
                                                    </template>
                                                </v-select>
                                            </v-col>
                                            <v-col cols="12" sm="12" md="3">
                                                <input
                                                    type="file"
                                                    class="d-none"
                                                    ref="uploader"
                                                    accept="image/*"
                                                    @change="selectFile">
                                                <v-text-field
                                                    :rules="imageRule"
                                                    :disabled="loading"
                                                    v-model="editedItem.slika"
                                                    label="Slika URL"
                                                    prepend-icon="mdi-cloud-upload"
                                                    @click:prepend="$refs.uploader.click()">
                                                </v-text-field>
                                            </v-col>
                                            <v-col cols="12" sm="12" md="3" class="d-flex justify-center align-center">
                                                <v-btn-toggle
                                                    v-model="editedItem.javna"
                                                    color="primary">
                                                    <v-btn class="toggle-option__company" :value="true">Javna</v-btn>
                                                    <v-btn class="toggle-option__company" :value="false">Privatna</v-btn>
                                                </v-btn-toggle>
                                            </v-col>
                                        </v-row>
                                        <v-row justify="center" align="center">
                                            <v-col cols="12">
                                                <v-textarea
                                                    label="Opis"
                                                    name="opis"
                                                    rows="3"
                                                    filled
                                                    outlined
                                                    shaped
                                                    auto-grow
                                                    v-model="editedItem.opis">
                                                </v-textarea>
                                            </v-col>
                                        </v-row>

                                        <v-divider />

                                        <v-row justify="center">
                                            <v-col cols="12" md="6" class="offline" :class="{'pr-6 divider-fit': mdAndUp}">
                                                <v-list subheader>
                                                    <!-- Offline -->
                                                    <v-subheader class="subheading pa-0">Offline</v-subheader>
                                                    <v-list-item class="elevation-2 my-4">
                                                        <v-list-item-content>
                                                            <span class="body-1">Poseduje pravilnik ZZPL</span>
                                                        </v-list-item-content>
                                                        <v-list-item-action class="align-self-center">
                                                            <v-btn-toggle
                                                                v-model="editedItem.poseduje_pravilnik_zzpl"
                                                                :color="editedItem.poseduje_pravilnik_zzpl ? 'primary' : 'error'">
                                                                <v-btn class="toggle-option" :value="true">Da</v-btn>
                                                                <v-btn class="toggle-option" :value="false">Ne</v-btn>
                                                            </v-btn-toggle>
                                                        </v-list-item-action>
                                                    </v-list-item>
                                                    <v-list-item class="elevation-2 my-4">
                                                        <v-list-item-content>
                                                            <span class="body-1">Adekvatan pravilnik</span>
                                                        </v-list-item-content>
                                                        <v-list-item-action class="align-self-center">
                                                            <v-btn-toggle
                                                                v-model="editedItem.adekvatan_pravilnik_zzpl"
                                                                :color="editedItem.adekvatan_pravilnik_zzpl ? 'primary' : 'error'">
                                                                <v-btn class="toggle-option" :value="true">Da</v-btn>
                                                                <v-btn class="toggle-option" :value="false">Ne</v-btn>
                                                            </v-btn-toggle>
                                                        </v-list-item-action>
                                                    </v-list-item>
                                                    <v-list-item class="elevation-2 my-4">
                                                        <v-list-item-content>
                                                            <span class="body-1">Odgovara na kontakt u vezi sa ZZPL</span>
                                                        </v-list-item-content>
                                                        <v-list-item-action class="align-self-center">
                                                            <v-btn-toggle
                                                                v-model="editedItem.odgovara_na_kontakt_zzpl"
                                                                :color="editedItem.odgovara_na_kontakt_zzpl ? 'primary' : 'error'">
                                                                <v-btn class="toggle-option" :value="true">Da</v-btn>
                                                                <v-btn class="toggle-option" :value="false">Ne</v-btn>
                                                            </v-btn-toggle>
                                                        </v-list-item-action>
                                                    </v-list-item>
                                                    <v-list-item class="elevation-2 my-4">
                                                        <v-list-item-content>
                                                            <span class="body-1">Transparentan u radu sa zaštitom podataka</span>
                                                        </v-list-item-content>
                                                        <v-list-item-action class="align-self-center">
                                                            <v-btn-toggle
                                                                v-model="editedItem.transparentan_u_radu_sa_zp"
                                                                :color="editedItem.transparentan_u_radu_sa_zp ? 'primary' : 'error'">
                                                                <v-btn class="toggle-option" :value="true">Da</v-btn>
                                                                <v-btn class="toggle-option" :value="false">Ne</v-btn>
                                                            </v-btn-toggle>
                                                        </v-list-item-action>
                                                    </v-list-item>
                                                    <v-list-item class="elevation-2 my-4">
                                                        <v-list-item-content>
                                                            <span class="body-1">Ima imenovano lice za ZZPL</span>
                                                        </v-list-item-content>
                                                        <v-list-item-action class="align-self-center">
                                                            <v-btn-toggle
                                                                v-model="editedItem.ima_imenovano_lice_zzpl"
                                                                :color="editedItem.ima_imenovano_lice_zzpl ? 'primary' : 'error'">
                                                                <v-btn class="toggle-option" :value="true">Da</v-btn>
                                                                <v-btn class="toggle-option" :value="false">Ne</v-btn>
                                                            </v-btn-toggle>
                                                        </v-list-item-action>
                                                    </v-list-item>
                                                </v-list>
                                            </v-col>

                                            <v-divider vertical class="d-none d-md-block" />

                                            <!-- Online -->
                                            <v-col cols="12" md="6" class="online" :class="{'pl-6': mdAndUp}">
                                                <v-subheader class="subheading pa-0">Online</v-subheader>
                                                <v-text-field
                                                    :rules="urlRule"
                                                    v-model="editedItem.website_url"
                                                    label="Website URL">
                                                </v-text-field>
                                                <v-text-field
                                                    :rules="urlRule"
                                                    v-model="editedItem.politika_privatnosti.url"
                                                    label="Politika privatnosti URL">
                                                </v-text-field>
                                                <v-list-item class="elevation-2 my-4">
                                                    <v-list-item-content
                                                        class="body-1"
                                                        :class="{'text--secondary' : invalidPrivacyPolicy}">
                                                        <span>Adekvatna politika privatnosti</span>
                                                    </v-list-item-content>
                                                    <v-list-item-action class="align-self-center">
                                                        <v-btn-toggle
                                                            v-model="editedItem.politika_privatnosti.adekvatna"
                                                            :color="editedItem.politika_privatnosti.adekvatna ? 'primary' : 'error'">
                                                            <v-btn
                                                                :value="true"
                                                                class="toggle-option"
                                                                :disabled="invalidPrivacyPolicy">
                                                                Da
                                                            </v-btn>
                                                            <v-btn
                                                                :value="false"
                                                                class="toggle-option"
                                                                :disabled="invalidPrivacyPolicy">
                                                                Ne
                                                            </v-btn>
                                                        </v-btn-toggle>
                                                    </v-list-item-action>
                                                </v-list-item>
                                                <v-list-item class="elevation-2 my-4">
                                                    <v-list-item-content>
                                                        <span class="body-1">Poseduje pravilnik ZZPL na sajtu</span>
                                                    </v-list-item-content>
                                                    <v-list-item-action class="align-self-center">
                                                        <v-btn-toggle
                                                            v-model="editedItem.poseduje_pravilnik_zzpl_website"
                                                            :color="editedItem.poseduje_pravilnik_zzpl_website ? 'primary' : 'error'">
                                                            <v-btn class="toggle-option" :value="true">Da</v-btn>
                                                            <v-btn class="toggle-option" :value="false">Ne</v-btn>
                                                        </v-btn-toggle>
                                                    </v-list-item-action>
                                                </v-list-item>
                                            </v-col>
                                        </v-row>
                                        <v-divider />
                                        <div class="py-6 text-right" v-show="!mdAndUp">
                                            <v-btn :disabled="loading" large color="primary" @click="save">Save</v-btn>
                                        </div>
                                    </v-container>
                                </v-form>
                            </v-card-text>
                        </v-card>
                    </v-dialog>

                    <v-btn
                        text
                        icon
                        class="ml-2"
                        :disabled="itemsPerPage < 0"
                        @click="initialize({refreshPage: true})">
                        <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                </v-toolbar>
            </template>

            <template v-slot:[`body.prepend`]="{ headers }">
                <tr class="mx-0 px-0">
                    <td :colspan="headers.length" class="mx-0 px-0">
                        <div class="d-md-flex flex-md-row">
                            <v-select
                                v-model="companyType"
                                :items="dropdownOptions"
                                class="mx-0 px-0 rounded-0 flex-md-grow-0"
                                single-line hide-details filled flat>
                            </v-select>
                            <v-text-field
                                v-model="search"
                                label="Search"
                                class="mx-0 px-0 rounded-0 flex-md-grow-1"
                                append-icon="mdi-magnify"
                                @click:append="searchCompany"
                                @keydown.enter="searchCompany"
                                single-line hide-details filled flat>
                            </v-text-field>
                        </div>
                    </td>
                </tr>
            </template>

            <template v-slot:[`item.score`]="{ value }">
                <v-icon :color="scoreColor(value).color">mdi-checkbox-blank-circle</v-icon>
            </template>

            <template v-slot:[`item.date_added`]="{ value }">
                {{ value && !isNaN(value) ? new Date(Number(value.seconds) * 1000).toLocaleString('en-UK') : 'Invalid Date' }}
            </template>

            <template v-slot:[`item.date_updated`]="{ value }">
                {{ value && !isNaN(value) ? new Date(Number(value.seconds) * 1000).toLocaleString('en-UK') : 'Not Updated' }}
            </template>

            <template v-slot:[`item.ready_for_publish`]="{ value }">
                <v-icon :color="value ? 'success': 'error'">{{ value ? 'mdi-check' : 'mdi-close '}}</v-icon>
            </template>

            <template v-slot:[`item.published`]="{ item, value }">
                <v-switch
                    :input-value="value"
                    :disabled="!item.ready_for_publish || loading || !user.admin"
                    color="primary"
                    hide-details
                    class="ma-0 pt-0"
                    @change="publishItem(item, value)">
                </v-switch>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                    icon
                    text
                    class="mr-2"
                    :disabled="loading"
                    @click="editItem(item)">
                    <v-icon medium>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                    icon
                    text
                    :disabled="loading || !user.admin"
                    @click="deleteItemDialog(item)">
                    <v-icon medium color="error">mdi-delete</v-icon>
                </v-btn>
            </template>

            <template v-slot:no-data>
                <p class="pt-4 mb-2">There is no such company</p>
                <v-btn color="primary" class="my-2" @click="initialize({refreshPage: true})">Refresh</v-btn>
            </template>

            <template v-slot:footer.prepend>
                <div class="d-flex align-center ma-2" v-if="mdAndUp">
                    <v-icon small>mdi-information-outline</v-icon>
                    <span class="ml-2 text--secondary">Use pagination arrows and search field to load more companies from database.</span>
                </div>
            </template>

        </v-data-table>

        <SimpleDialog
            :visible="deleteDialog"
            title="Delete Company"
            :message="`You're about to delete ${itemToDelete.naziv} from database.`"
            yes="Delete"
            no="Cancel"
            @on-yes="deleteItem(itemToDelete)"
            @on-no="deleteDialog = false">
        </SimpleDialog>

        <SimpleDialog
            :visible="deleteCityDialog"
            title="Delete City"
            :message="`You're about to delete '${itemToDelete}' from cities.`"
            yes="Delete"
            no="Cancel"
            @on-yes="addOrDeleteCity(itemToDelete, 'delete')"
            @on-no="deleteCityDialog = false">
        </SimpleDialog>

        <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
            <span>{{ snackText }}</span>
            <template v-slot:action="{ attrs }">
                <v-icon v-bind="attrs" text @click="snack = false">mdi-close</v-icon>
            </template>
        </v-snackbar>
    </div>
</template>

<script>
import firebase from 'firebase/app';
import 'firebase/storage';
import 'firebase/firestore';
import { mapActions, mapGetters } from 'vuex';
import SimpleDialog from "../../components/SimpleDialog";
import {showSnack} from "@/components/mixins/showSnack";
import {scoreColor} from "@/components/mixins/scoreColor";

const storage = firebase.storage;
const imgRegex = /^(?:(?:https?|ftp):\/\/)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/;

export default {
    name: 'Companies',

    components: {
        SimpleDialog
    },

    mixins: [showSnack, scoreColor],

    data() {
        return {
            searchCity: '',
            cities: [],
            deleteCityDialog: false,
            editCompany: false,
            deleteDialog: false,
            itemToDelete: {},
            loading: true,
            search: '',
            page: 1,
            pageMax: 1,
            itemsPerPage: 5,
            totalLoadedCompanies: null,
            sortBy: ['date_added'],
            searchMode: false,
            sortDesc: [true],
            headers: [
                {
                    text: 'Score',
                    value: 'score',
                    sortable: false,
                    align: 'left'
                },
                {
                    text: 'Company Name',
                    align: 'start',
                    sortable: true,
                    value: 'naziv',
                    cellClass: 'body-2 font-weight-medium'
                },
                {
                    text: 'City',
                    align: 'center',
                    sortable: false,
                    value: 'grad'
                },
                {
                    text: 'Date Added',
                    value: 'date_added',
                    sortable: true,
                    align: 'center'
                },
                {
                    text: 'Date Updated',
                    value: 'date_updated',
                    sortable: true,
                    align: 'center'
                },
                {
                    text: 'Ready',
                    value: 'ready_for_publish',
                    sortable: true,
                    align: 'center'
                },
                {
                    text: 'Published',
                    value: 'published',
                    sortable: true,
                    align: 'left'
                },
                {
                    text: 'Actions',
                    value: 'actions',
                    sortable: false,
                    align: 'right'
                },
            ],
            footerProps: {
                itemsPerPageOptions: [5, 10, -1],
                itemsPerPageText: 'Companies per page',
                showCurrentPage: true
            },
            companyType: 'All',
            dropdownOptions: ['All', 'Public', 'Private'],

            editedIndex: -1,
            editedItem: {},
            companySchema: {
                naziv: '',
                grad: '',
                slika: '',
                javna: null,
                opis: '',
                poseduje_pravilnik_zzpl: null,
                adekvatan_pravilnik_zzpl: null,
                odgovara_na_kontakt_zzpl: null,
                transparentan_u_radu_sa_zp: null,
                ima_imenovano_lice_zzpl: null,
                website_url: '',
                politika_privatnosti: {
                    url: '',
                    adekvatna: null
                },
                poseduje_pravilnik_zzpl_website: null,
                social: {
                    rating: null,
                    comments: [],
                },
                date_added: null,
                date_updated: null,
                date_published: null,
                meta: {
                    added_by: '',
                    updated_by: '',
                    published_by: ''
                },
                ready_for_publish: false,
                published: false,
                score: null,
                keywords: []
            },
            titleRule: [
                v => !!v || 'Company name is required'
            ],
            cityRule: [
                v => !!v || 'City is required'
            ],
            imageRule: [
                v => v ? (imgRegex.test(v) || 'Image link must be valid') : true
            ],
            urlRule: [
                v => v ? this.isValidHttpUrl(v) || 'Entered URL must be valid' : true
            ]
        };
    },

    computed: {
        ...mapGetters({
            companies: 'companies/getCompanies',
            companiesUtils: 'dashboard/companiesUtils',
            user: 'auth/user'
        }),
        formTitle() {
            return this.editedIndex === -1 ? 'New Company' : 'Edit Company';
        },
        mdAndUp() {
            return this.$vuetify.breakpoint.mdAndUp;
        },
        invalidPrivacyPolicy() {
            return !this.editedItem.politika_privatnosti.url || !this.isValidHttpUrl(this.editedItem.politika_privatnosti.url);
        }
    },

    watch: {
        editCompany(val) {
            val || this.close();
        },
        sortBy() {
            this.initialize({refreshPage: true});
        },
        sortDesc() {
            this.initialize({refreshPage: true});
        }
    },

    created() {
        this.editedItem = Object.assign({}, this.companySchema);
        this.initialize({refreshPage: true});
    },

    beforeRouteLeave(to, from, next) {
        if (this.editCompany) {
            this.close();
            next(false);
        } else {
            next();
        }
    },

    methods: {
        ...mapActions({
            loadCompanies: 'companies/loadCompanies',
            searchCompanies: 'companies/searchCompanies',
            addCompany: 'companies/addCompany',
            updateCompany: 'companies/updateCompany',
            removeCompany: 'companies/removeCompany',
            addCompaniesUtils: 'dashboard/addCompaniesUtils',
            removeCompaniesUtils: 'dashboard/removeCompaniesUtils',
            updateCompaniesUtilsProp: 'dashboard/updateCompaniesUtilsProp'
        }),
        filterCity() {
            if (!this.searchCity) {
                this.cities = [...this.companiesUtils.cities];
                return;
            }
            this.cities = this.companiesUtils.cities.filter(city => {
                return city.toLowerCase().indexOf(this.searchCity.toLowerCase()) >= 0;
            });
        },
        async addOrDeleteCity(value, action) {
            try {
                await this.updateCompaniesUtilsProp({ prop: 'cities', value, action });
                this.showSnack(`${value} is ${action}ed to cities.`, 'success');
            } catch (e) {
                this.showSnack(`${value} is not ${action}ed to cities.`, 'error');
                console.error(e);
            } finally {
                this.deleteCityDialog = false;
            }
        },
        cityDialog(item) {
            this.deleteCityDialog = true;
            this.itemToDelete = item;
        },
        async searchCompany() {
            this.loading = true;
            this.resetPagination();
            try {
                if (this.search) {
                    this.searchMode = true;
                    await this.searchCompanies({ type: this.companyType, search: this.search});
                } else {
                    this.searchMode = false;
                    await this.initialize({refreshPage: true});
                }
            } catch (e) {
                console.error(e);
            }
            this.loading = false;
        },
        async initialize(args) {
            this.loading = true;
            try {
                const { refreshPage } = args;
                const { itemsPerPage, sortBy, sortDesc, companyType } = this;
                const orderBy = [sortBy[0] || 'date_added', sortDesc[0] ? 'desc' : 'asc'];
                const params = {
                    companyType,
                    orderBy,
                    itemsPerPage,
                    refreshPage
                }
                await this.loadCompanies(params);
            } catch (e) {
                console.error(e);
            }
            this.loading = false;
        },
        editItem(item) {
            this.editedIndex = this.companies.indexOf(item);
            this.editedItem = Object.assign({}, item);
            this.editCompany = true;
        },
        close() {
            this.editCompany = false;
            this.$nextTick(() => {
                this.editedItem = Object.assign({}, this.companySchema);
                this.editedIndex = -1;
            });
        },
        deleteItemDialog(item) {
            this.deleteDialog = true;
            this.itemToDelete = Object.assign({}, item);
        },
        async deleteItem(item) {
            this.loading = true;
            try {
                await this.removeCompany(item);
                if (item.slika && !/otisak-placeholder/.test(item.slika)) {
                    await storage().refFromURL(item.slika).delete();
                }
                this.removeCompaniesUtils('length');
                this.showSnack(`${item.naziv} has been deleted from database`, 'success');
            } catch (e) {
                this.showSnack(`${item.naziv} could not be deleted`, 'error');
                console.error(e);
            } finally {
                this.deleteDialog = false;
                this.itemToDelete = {};
                this.loading = false;
            }
        },
        async publishItem(item, value) {
            this.loading = true;
            try {
                this.editedIndex = this.companies.indexOf(item);
                this.editedItem = Object.assign({}, item);
                this.editedItem.keywords = this.extractKeywords(this.editedItem.naziv);
                this.editedItem.date_published = firebase.firestore.Timestamp.fromDate(new Date());
                if (this.editedItem.ready_for_publish && this.editedItem.score !== null) {
                    this.editedItem.score = this.scoreCompute(this.editedItem);
                }
                this.editedItem.meta.published_by = this.user.data.uid;
                this.editedItem.published = !value;
                await this.updateCompany({index: this.editedIndex, company: this.editedItem});
                this.showSnack(`${this.editedItem.naziv} is now ${!value ? 'published' : 'unpublished'}`, 'success');
            } catch (e) {
                this.showSnack(`${this.editedItem.naziv} can't be ${!value ? 'published' : 'unpublished'}`, 'error');
                console.error(e);
            } finally {
                this.loading = false;
            }
        },
        scoreCompute(item) {
            // red - 0-3, yellow - 4-7, green - 8-11
            const scoringFields = [
                'poseduje_pravilnik_zzpl', // 1
                'adekvatan_pravilnik_zzpl', // 2
                'odgovara_na_kontakt_zzpl', // 1
                'transparentan_u_radu_sa_zp', // 1
                'ima_imenovano_lice_zzpl', // 1
                'website_url', // 1
                'politika_privatnosti', // url 1, adekvatna 2
                'poseduje_pravilnik_zzpl_website'  // 1
            ];
            let score = 0;
            for (const prop in item) {
                if (scoringFields.includes(prop)) {
                    if (typeof item[prop] === 'boolean' && item[prop] === true) {
                        if (prop === 'adekvatan_pravilnik_zzpl') {
                            score += 2;
                        } else {
                            score += 1;
                        }
                    } else if (prop === 'politika_privatnosti') {
                        if (item[prop].url) {
                            score += 1;
                        }
                        if (item[prop].adekvatna === true) {
                            score += 2;
                        }
                    } else if (prop === 'website_url' && item[prop]) {
                        score += 1;
                    }
                }
            }
            return score;
        },
        readyForPublish(item) {
            const mandatoryFields = [
                'javna', 'poseduje_pravilnik_zzpl', 'adekvatan_pravilnik_zzpl', 'odgovara_na_kontakt_zzpl',
                'transparentan_u_radu_sa_zp', 'ima_imenovano_lice_zzpl', 'poseduje_pravilnik_zzpl_website'
            ];
            return !Object.entries(item).some(([key, value]) => mandatoryFields.includes(key) && value === null);
        },
        async save() {
            if (!this.$refs.dialogForm.validate()) {
                return;
            }
            this.loading = true;
            if (this.editedIndex > -1) {
                try {
                    this.editedItem.ready_for_publish = this.readyForPublish(this.editedItem);
                    if (this.editedItem.ready_for_publish) {
                        this.editedItem.score = this.scoreCompute(this.editedItem);
                    }
                    this.editedItem.keywords = this.extractKeywords(this.editedItem.naziv);
                    this.editedItem.date_updated = firebase.firestore.Timestamp.fromDate(new Date());
                    this.editedItem.meta.updated_by = this.user.data.uid;
                    await this.updateCompany({index: this.editedIndex, company: this.editedItem});
                    this.showSnack(`${this.editedItem.naziv} has been updated`, 'success');
                } catch (e) {
                    this.showSnack(`${this.editedItem.naziv} couldn't be updated`, 'error');
                    console.error(e);
                } finally {
                    this.close();
                    this.loading = false;
                }
            } else {
                try {
                    this.editedItem.ready_for_publish = this.readyForPublish(this.editedItem);
                    if (this.editedItem.ready_for_publish) {
                        this.editedItem.score = this.scoreCompute(this.editedItem);
                    }
                    this.editedItem.keywords = this.extractKeywords(this.editedItem.naziv);
                    this.editedItem.date_added = firebase.firestore.Timestamp.fromDate(new Date());
                    this.editedItem.meta.added_by = this.user.data.uid;
                    await this.addCompany(this.editedItem);
                    await this.addCompaniesUtils('length');
                    this.showSnack(`${this.editedItem.naziv} is added to database.`, 'success');
                } catch (e) {
                    this.showSnack(`${this.editedItem.naziv} couldn't be added to database.`, 'error');
                    console.error(e);
                } finally {
                    this.close();
                    this.loading = false;
                }
            }
        },
        extractKeywords(string) {
            const keywords = string.toLowerCase().split(/[ -]+/).filter(word => {
                if (word.length > 2 || !isNaN(parseInt(word, 10))) {
                    return word;
                }
            });
            const original = keywords.map(word => word.replace(/[^a-z0-9čšćđž]/g, ''));
            const converted = original.map(word => {
                if (/[čšćđž]/.test(word)) {
                    return word.split('').map(ch => {
                        const replaceIndex = ['č', 'ć', 'đ', 'š', 'ž'].indexOf(ch);
                        if (replaceIndex !== -1) {
                            return ['c', 'c', 'dj', 's', 'z'][replaceIndex];
                        } else {
                            return ch;
                        }
                    }).join('');
                }
            }).filter(Boolean);
            return [...original, ...converted];
        },
        selectFile() {
            let file = this.$refs.uploader.files[0];
            if (file && file.name) {
                this.loading = true;
                const ref = storage().ref().child(`company_images/${file.name}`);
                const vm = this;
                ref.put(file).then(snapshot => {
                    this.loading = false;
                    if (snapshot.state === 'success') {
                        snapshot.ref.getDownloadURL().then(downloadURL => {
                            vm.editedItem.slika = downloadURL;
                        });
                    } else {
                        this.showSnack('Image could not be uploaded', 'error');
                    }
                    file = null;
                });
            }
        },
        isValidHttpUrl(string) {
            let url;
            try {
                url = new URL(string);
            } catch (e) {
                return false;
            }
            return url.protocol === "http:" || url.protocol === "https:";
        },
        getNextPage(event) {
            this.pageMax = Math.ceil(this.companies.length / this.itemsPerPage);
            if (event === this.pageMax && !this.search) {
                this.initialize({refreshPage: false});
            }
            this.page = event;
        },
        resetPagination() {
            this.page = 1;
            this.pageMax = 1;
        }
    }
};
</script>

<style lang="scss">
#companies-view-edit {
    .divider-fit {
        max-width: calc(50% - 2px);
    }
    .subheading {
        font-size: 1.2rem;
    }
    .toggle-option {
        width: 5rem;
        &__company {
            width: 6rem;
        }
    }
    #dialog-container .row {
        margin: 0;
    }
}
</style>
